import { Toaster as SonnerToaster, toast as sonnerToast } from 'sonner';
import { tokens } from '../styles/tokens.js';

/**
 * Wrap sonner with our brand colors. Mount `<Toaster />` once near the app root.
 * Use `toast.success(...)`, `toast.error(...)` etc. anywhere afterwards.
 */
export const Toaster = (props: Parameters<typeof SonnerToaster>[0]) => (
  <SonnerToaster
    position="top-right"
    richColors
    closeButton
    visibleToasts={2}
    toastOptions={{
      style: {
        background: tokens.surface,
        color: tokens.fg,
        border: `1px solid ${tokens.border}`,
        fontFamily: 'inherit',
      },
    }}
    {...props}
  />
);

/** Quanto um aviso de sucesso ou informação fica na tela. */
export const TOAST_MS = 4_000;

type SonnerToast = typeof sonnerToast;
type Show = SonnerToast['success'];

/**
 * No celular, um toque na lista conta como "mouse em cima" para o sonner, que
 * pausa a contagem até um "mouse saiu" que nunca chega: os avisos ficavam na
 * tela e se empilhavam (#510). Aqui o fechamento tem relógio próprio.
 */
const timed =
  (show: Show): Show =>
  (message, data) => {
    const duration = data?.duration ?? TOAST_MS;
    const id = show(message, { ...data, duration });
    if (Number.isFinite(duration)) {
      setTimeout(() => sonnerToast.dismiss(id), duration + 500);
    }
    return id;
  };

let lastErrorId: string | number | undefined;

/** Erro fica até alguém fechar, mas só o último: fechar um não traz o anterior (#510). */
const error: Show = (message, data) => {
  if (lastErrorId !== undefined) sonnerToast.dismiss(lastErrorId);
  lastErrorId = sonnerToast.error(message, { duration: Number.POSITIVE_INFINITY, ...data });
  return lastErrorId;
};

export const toast: SonnerToast = Object.assign(
  timed(sonnerToast as unknown as Show),
  sonnerToast,
  {
    success: timed(sonnerToast.success),
    info: timed(sonnerToast.info),
    warning: timed(sonnerToast.warning),
    message: timed(sonnerToast.message),
    error,
  },
);
